/**
 * Capa de servicios: un único punto de consumo de la API REST, agrupado por dominio.
 * Los componentes nunca llaman a fetch directamente.
 */
import type { Keystroke, LanguageCode, TestKind, TestMode } from '@manitype/core';
import { http } from '../lib/api';
import type {
  AchievementInfo, Certificate, Challenge, CourseDetail, CourseSummary, EligibleResult, EvolutionPoint, GameInfo, GameSubmitOutcome,
  GlobalStats, IssuedTest, KeyStatRow, Leaderboard, LessonDetail, Me, PublicProfile, PublicSettings, RaceRoomSummary, ResultRow,
  StatsSummary, SubmitOutcome, TrainingReport, UserSettings,
} from './types';

export interface CreateTestRequest {
  kind?: Exclude<TestKind, 'race'>;
  mode?: TestMode;
  language?: LanguageCode;
  durationSeconds?: number;
  wordCount?: number;
  difficulty?: 'easy' | 'normal' | 'hard';
  letters?: string;
  weakKeys?: string[];
  customText?: string;
  customAsWordList?: boolean;
  stopOnError?: boolean;
  exerciseId?: number;
  challengeId?: string;
}

export interface StatsFilter {
  language?: LanguageCode;
  mode?: TestMode;
  kind?: TestKind;
  from?: string;
  to?: string;
}

export const authApi = {
  me: () => http.get<{ user: Me | null }>('/auth/me'),
  login: (identifier: string, password: string) => http.post<{ user: Me }>('/auth/login', { identifier, password }),
  register: (body: { username: string; email: string; password: string; uiLanguage?: LanguageCode; exerciseLanguage?: LanguageCode }) =>
    http.post<{ user: Me }>('/auth/register', body),
  logout: () => http.post<void>('/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) => http.post<void>('/me/password', { currentPassword, newPassword }),
  deleteAccount: (password: string) => http.del<void>('/me', { password }),
};

export const usersApi = {
  profile: (username: string) => http.get<{ profile: PublicProfile }>(`/users/${encodeURIComponent(username)}`),
  updateProfile: (body: Partial<{ displayName: string; avatar: string; avatarColor: string; country: string | null; bio: string; visibility: 'public' | 'private'; showStats: boolean }>) =>
    http.patch<{ user: Me }>('/me/profile', body),
  updateSettings: (body: Partial<UserSettings>) => http.put<{ user: Me }>('/me/settings', body),
};

export const testsApi = {
  create: (body: CreateTestRequest) => http.post<{ test: IssuedTest }>('/tests', body),
  submit: (id: string, log: Keystroke[]) => http.post<SubmitOutcome>(`/tests/${id}/result`, { log }),
  results: (query: { kind?: TestKind; mode?: TestMode; language?: LanguageCode; limit?: number; offset?: number }) =>
    http.get<{ results: ResultRow[] }>('/me/results', query),
};

export const textsApi = {
  random: (language: LanguageCode, type: 'phrase' | 'paragraph', count = 10) =>
    http.get<{ texts: { id: number; content: string; title: string | null }[] }>('/texts/random', { language, type, count }),
};

export const statsApi = {
  summary: (filter: StatsFilter) => http.get<{ summary: StatsSummary }>('/me/stats/summary', { ...filter }),
  evolution: (filter: StatsFilter, granularity: 'day' | 'week' | 'month') =>
    http.get<{ points: EvolutionPoint[] }>('/me/stats/evolution', { ...filter, granularity }),
  keys: (language: LanguageCode) => http.get<{ keys: KeyStatRow[] }>('/me/stats/keys', { language }),
  training: (language: LanguageCode) => http.get<{ report: TrainingReport }>('/me/training', { language }),
  global: () => http.get<{ stats: GlobalStats }>('/stats/global'),
  publicSettings: () => http.get<{ settings: PublicSettings }>('/settings/public'),
};

export const academyApi = {
  courses: (language?: LanguageCode) => http.get<{ courses: CourseSummary[] }>('/courses', { language }),
  course: (slug: string) => http.get<{ course: CourseDetail }>(`/courses/${slug}`),
  lesson: (id: number) => http.get<{ lesson: LessonDetail }>(`/lessons/${id}`),
};

export const competeApi = {
  leaderboard: (query: { type: Leaderboard['type']; language: LanguageCode; duration: number; period: Leaderboard['period'] }) =>
    http.get<{ leaderboard: Leaderboard }>('/leaderboards', query),
  records: (language: LanguageCode) => http.get<{ records: Record<string, Leaderboard['entries']> }>('/records', { language }),
  challenges: (language: LanguageCode) => http.get<{ challenges: Challenge[] }>('/challenges', { language }),
  challenge: (id: string) => http.get<{ challenge: Challenge }>(`/challenges/${id}`),
  achievements: () => http.get<{ achievements: AchievementInfo[] }>('/achievements'),
};

export const gamesApi = {
  list: () => http.get<{ games: GameInfo[] }>('/games'),
  submit: (slug: string, body: { language: LanguageCode; score: number; durationMs: number; wordsTyped: number; charsTyped: number; wpm: number; accuracy: number; levelReached: number }) =>
    http.post<GameSubmitOutcome>(`/games/${slug}/results`, body),
  leaderboard: (slug: string, period: 'all' | 'weekly' | 'monthly' = 'all') =>
    http.get<{ leaderboard: { entries: { username: string; country: string | null; xp: number; score: number; level_reached: number; wpm: number; achieved_at: string }[] } }>(
      `/games/${slug}/leaderboard`,
      { period },
    ),
};

export const racesApi = {
  rooms: () => http.get<{ rooms: RaceRoomSummary[] }>('/races'),
  create: (language: LanguageCode, isPrivate: boolean) => http.post<{ code: string }>('/races', { language, isPrivate }),
  /** Prepara la identidad de invitado (cookie HttpOnly) antes de entrar en una sala. */
  guest: () => http.post<{ guest: boolean }>('/races/guest', {}),
  history: () => http.get<{ races: { id: string; code: string; language: string; started_at: string; position: number | null; wpm: number | null; accuracy: number | null; participants: number }[] }>('/me/races'),
};

export const certificatesApi = {
  mine: () => http.get<{ certificates: Certificate[]; eligible: EligibleResult[] }>('/me/certificates'),
  issue: (resultId: string) => http.post<{ certificate: Certificate }>('/me/certificates', { resultId }),
  verify: (code: string) => http.get<{ certificate: Certificate }>(`/certificates/${encodeURIComponent(code)}`),
};

export const adminApi = {
  overview: () => http.get<{ overview: Record<string, number> }>('/admin/overview'),
  users: (q: string, offset = 0) =>
    http.get<{ total: number; items: { id: string; username: string; email: string; role: string; status: string; created_at: string; last_login_at: string | null; xp: number }[] }>(
      '/admin/users',
      { q, offset, limit: 50 },
    ),
  updateUser: (id: string, body: { status?: 'active' | 'suspended'; role?: string }) => http.patch<void>(`/admin/users/${id}`, body),
  flagged: () => http.get<{ items: Record<string, unknown>[] }>('/admin/reports/results'),
  review: (id: string, decision: 'approve' | 'reject') => http.post<void>(`/admin/reports/results/${id}`, { decision }),
  security: () => http.get<{ items: Record<string, unknown>[] }>('/admin/reports/security'),
  audit: () => http.get<{ items: Record<string, unknown>[] }>('/admin/audit'),
  certificates: (q: string) => http.get<{ items: Record<string, unknown>[] }>('/admin/certificates', { q }),
  revokeCertificate: (id: string, reason: string) => http.post<void>(`/admin/certificates/${id}/revoke`, { reason }),
  settings: () => http.get<{ settings: Record<string, unknown> }>('/admin/settings'),
  updateSettings: (body: Record<string, unknown>) => http.put<{ settings: Record<string, unknown> }>('/admin/settings', body),
  list: (resource: string, query: Record<string, string | number>) => http.get<{ items: Record<string, unknown>[]; total: number }>(`/admin/content/${resource}`, query),
  create: (resource: string, body: Record<string, unknown>) => http.post<{ item: Record<string, unknown> }>(`/admin/content/${resource}`, body),
  update: (resource: string, id: string, body: Record<string, unknown>) => http.patch<{ item: Record<string, unknown> }>(`/admin/content/${resource}/${id}`, body),
  remove: (resource: string, id: string) => http.del<void>(`/admin/content/${resource}/${id}`),
};
